import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { useAuth } from "@/app/providers/AuthProvider";
import { ModuleOffScreen } from "@/app/routes/ModuleOffScreen";
import { NotFoundScreen } from "@/app/routes/NotFoundScreen";
import { SkBlock, SkLine } from "@altek/ui/components/feedback/Skeleton";

/**
 * The gate every authenticated route sits behind.
 *
 * WHY THE GATE IS ON THE ROUTE AND NOT INSIDE THE PAGE. A check inside the component runs
 * AFTER the page mounted, which means its queries already fired — so an unauthorised visit
 * makes the requests, gets refused, and then hides a result that was never allowed. Gating the
 * route means the page never renders and never asks.
 *
 * A MISSING PERMISSION RENDERS "PAGE NOT FOUND", not "access denied". Same reasoning as the
 * scope rule on the server: telling somebody a page exists but is closed to them confirms the
 * existence of something they were not meant to know about. The menu already omitted the item;
 * a URL typed by hand gets the same answer a wrong URL gets.
 */
export function ProtectedRoute({
  children,
  permission,
  all,
  anyOf,
  module,
  installation,
  requireAnyRecord = false,
}: {
  children: ReactNode;
  permission?: string;
  all?: readonly string[];
  /**
   * HERHANGİ BİRİ yeterli — `all`'ın tersi.
   *
   * Sekmeli bir BÖLÜM için var: «Sistem» dört sekme taşıyor ve her sekmenin kendi anahtarı
   * var. Bölümü `all` ile kapatmak, yalnız otomasyon yetkisi olan birine — ki canlıda «Müdür»
   * ve «Patron» tam olarak bu — girebileceği bir sekmeyi kapatırdı. Tek anahtar yazmak da
   * olmaz: hangisini seçersek öbürünü tutan kişiyi dışarıda bırakır.
   *
   * Sekmenin KENDİ yetkisini bu kapı uygulamıyor, `AdminSection` uyguluyor; burada sorulan
   * soru yalnızca "bu bölümde bu kişiye gösterilecek bir şey var mı".
   */
  anyOf?: readonly string[];
  module?: string;
  /**
   * The shape of installation this screen belongs to — `"SHARED"` for the vendor's own
   * platform screens, omitted for everything else.
   *
   * WHY THE ROUTE REPEATS THE MENU'S RULE. The menu hides the Platform panel on a dedicated
   * installation, and hiding is all a menu can do; the path still resolves if it is typed. The
   * same half-fix already happened once in this file — `/hr/leave-requests` had been fixed in
   * the menu and not in the router, so a SELF-scoped grant still opened it by URL, and
   * `requireAnyRecord` exists because of it. Adding a second gate that a second URL walks
   * around would have repeated that exactly.
   */
  installation?: "SHARED" | "DEDICATED";
  /**
   * The screen is about OTHER PEOPLE's records, so a `SELF` grant is not enough.
   *
   * Set for every İK and Yönetim route, from the same panel rule the menu uses. Without it a
   * hand-typed `/hr/leave-requests` still rendered for somebody holding only
   * `hr.leave.read (SELF)` — the menu had been fixed and the URL had not, which is the half-fix
   * that makes a permission bug look solved.
   */
  requireAnyRecord?: boolean;
}) {
  const { status, permissions, session, endedBy } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    /**
     * The boot screen, and it is NOT the sign-in form.
     *
     * Rendering sign-in while the session is still being read flashes a login page at somebody
     * who is already signed in, on every reload. A shell-shaped skeleton says "loading" without
     * claiming anything about who they are.
     */
    return (
      <div className="grid min-h-dvh place-items-center bg-bg-app">
        <div className="w-full max-w-72 space-y-3" aria-busy="true" aria-label="Yükleniyor">
          <SkBlock className="mx-auto h-6 w-40" />
          <SkLine w="100%" />
          <SkLine w="70%" />
        </div>
      </div>
    );
  }

  if (status === "unauthenticated") {
    // `state` carries where they were going, so signing in returns them there rather than to
    // the dashboard — the difference between following a shared link and losing it.
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search, expired: endedBy === "expired" }}
      />
    );
  }

  const holdsAll = requireAnyRecord ? permissions.canAllForOthers : permissions.canAll;
  const holdsOne = requireAnyRecord ? permissions.canForOthers : permissions.can;

  const allowed = all
    ? holdsAll(all)
    : anyOf
      ? anyOf.some((key) => holdsOne(key))
      : permission
        ? holdsOne(permission)
        : true;

  const moduleOk = module ? permissions.isModuleEnabled(module) : true;

  // A screen that does not belong to this installation answers exactly as a missing permission
  // does — "page not found" — for the same reason: confirming it exists tells somebody about a
  // surface they were not meant to know about.
  const installationOk = installation ? session?.tenant.mode === installation : true;

  /**
   * Gerçek 404 ile AYNI bileşen (`NotFoundScreen`, 8 Ekim 2026): burada çıkışsız bir kopyası
   * vardı — «Başlangıca dön» yoktu ve iki ekranın farkı, saklanan sayfanın var olduğunu söylüyordu.
   */
  if (!allowed || !installationOk) {
    return <NotFoundScreen />;
  }
  /**
   * İzni olan kişi kapalı modülün adresinde «kapalı» görüyor (1 Kasım 2026, `ModuleOffScreen`): ekranı zaten
   * tanıyor. İzni olmayan yukarıda 404 aldı — ekranın varlığı ona yine söylenmiyor.
   */
  if (!moduleOk) {
    return <ModuleOffScreen />;
  }

  return <>{children}</>;
}
